import { Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { Dispatch } from '@reduxjs/toolkit';
import axios from 'axios';
import { useMemo, useState, VFC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import TriggerLiveTestPayloadEditor, {
  REACT_SELECT_PORTAL_ID_PREFIX,
} from 'dashboard/components/WorkflowControlPanel/TriggerLiveTest/TriggerLiveTestPayloadEditor';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Environment } from 'dashboard/models/environment';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import Banner from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { paragraphMixins } from 'design-system/mixins/text';
import { FieldSchemaType, JsonSchemaTypeToFieldType } from 'lib/fields';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema, JsonSchemaCore, JsonSchemaTypes } from 'lib/json-schema';
import { Permission } from 'lib/permissions';

const StyledCodeHighlight = styled(CodeHighlight)`
  border: 1px solid ${({ theme }) => theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const InstructionTextLarge = styled.div`
  ${paragraphMixins.md}
  padding-bottom: ${Spacing[2]};
`;

const InstructionTextSmall = styled.div`
  ${paragraphMixins.sm}
  padding-bottom: ${Spacing[2]};
`;

const EditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const WarningText = styled.div`
  ${paragraphMixins.md}
  color: red;
  padding-bottom: ${Spacing[2]};
  display: flex;
  justify-content: center;
`;

const APICallButtonContainer = styled.div`
  padding-bottom: ${Spacing[4]};
  display: flex;
  justify-content: space-between;
`;

const APIKeyInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: ${Spacing[2]};
  align-items: baseline;
`;

const StyledBanner = styled(Banner)`
  margin-top: ${Spacing[5]};
`;

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const samplePrimitiveResolver = (type: JsonSchemaTypes): boolean | number | string => {
  switch (type) {
    case JsonSchemaTypes.Boolean:
      return true;
    case JsonSchemaTypes.Integer:
      return 1;
    case JsonSchemaTypes.Number:
      return 0.0;
    case JsonSchemaTypes.String:
      return '';
  }
};

const isFile = (schema: JsonSchema): boolean => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const fieldType = schema.fieldType || JsonSchemaTypeToFieldType[schema.type];
  return fieldType === FieldSchemaType.File;
};

const containsFile = (parameterJson: Record<string, JsonSchema>): boolean => {
  return Object.entries(parameterJson).some(([_, variableType]) => {
    return isFile(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      variableType['type'] === JsonSchemaTypes.Array ? variableType.items : variableType,
    );
  });
};

const parameterJsonMapper = (
  parameterJson: Record<string, JsonSchemaCore>,
): Record<string, any> => {
  const outputJson: Record<string, unknown> = {};

  Object.entries(parameterJson).forEach(([variableName, variableType]) => {
    switch (variableType['type']) {
      case JsonSchemaTypes.Array:
        if (!isFile(variableType.items)) {
          outputJson[variableName] = [samplePrimitiveResolver(variableType.items.type)];
        }
        break;
      case JsonSchemaTypes.Object:
        if (!isFile(variableType)) {
          outputJson[variableName] = parameterJsonMapper(variableType.properties);
        }
        break;
      default:
        outputJson[variableName] = samplePrimitiveResolver(variableType.type);
        break;
    }
  });
  return outputJson;
};

export const postSampleTriggerRequest = async (
  testEndpoint: string,
  samplePayload: string,
  apiKey: string,
  dispatch: Dispatch,
) => {
  try {
    await axios({
      method: 'post',
      url: testEndpoint,
      data: { data: { attributes: JSON.parse(samplePayload) } },
      headers: {
        Authorization: `Bearer ${apiKey}`,
        accept: 'application/json',
      },
    });
    dispatch(showMessage('Request sent successfully', MessageType.Success));
  } catch (error) {
    dispatch(showMessage('Request failed to send, please try again later.', MessageType.Failure));
  }
};

interface TriggerLiveTestProps {
  apiEndpoint: string;
  parameterJson: Record<string, JsonSchemaCore>;
  isDirty: boolean;
  isCurrentPublishedVersion: boolean;
  transactionTypeId?: string;
}

const TriggerLiveTest: VFC<TriggerLiveTestProps> = ({
  apiEndpoint,
  parameterJson,
  isDirty,
  isCurrentPublishedVersion,
  transactionTypeId,
}) => {
  const dispatch = useDispatch();
  const [outputJson, setOutputJson] = useState<Record<string, unknown>>(() =>
    parameterJsonMapper(parameterJson),
  );
  const containsFileProperty = useMemo<boolean>(() => containsFile(parameterJson), [parameterJson]);
  const [isApiKeyHidden, { toggle: toggleAPIKeyHidden }] = useToggle(true);

  const [
    isSamplePayloadEditorOpen,
    { disable: closeSamplePayloadEditor, toggle: toggleSamplePayloadEditor },
  ] = useToggle(false);

  const testEndpoint = `${location.origin}${apiEndpoint}`;

  const canViewApiKey = usePermission(Permission.ApiKeyView);
  const apiKey =
    useSelector<RootState, Environment>((state) => state.currentEnvironment.currentEnvironment)
      ?.attributes['workflow-secret-key'] ?? '';
  const displayedApiKey = isApiKeyHidden ? '<API-KEY>' : apiKey;

  const samplePayload = transactionTypeId
    ? JSON.stringify({ 'transaction-type-id': transactionTypeId, fields: outputJson })
    : JSON.stringify(outputJson);

  const sampleCurlString = `curl -X POST \\\n-H "Content-Type: application/json" \\\n-H "Authorization: Bearer ${displayedApiKey}" \\\n-d '{"data": {"attributes": ${samplePayload}} }' \\\n${location.origin}${apiEndpoint}`;

  const handlePostTestPayload = () => {
    postSampleTriggerRequest(testEndpoint, samplePayload, apiKey, dispatch);
  };

  return (
    <>
      {isSamplePayloadEditorOpen && (
        <MaximizablePanel
          title='Payload'
          outsideExceptionalIdPrefixes={[REACT_SELECT_PORTAL_ID_PREFIX]}
          onOutsideClickAndEscPress={closeSamplePayloadEditor}
        >
          {(fullScreen: boolean) => (
            <EditorWrapper style={{ height: fullScreen ? 'calc(100% - 48px)' : 'auto' }}>
              <APICallButtonContainer>
                Name / Type / Value
                <div>
                  <Button
                    id='trigger-config-api__http-post-inside-modal-button'
                    onClick={handlePostTestPayload}
                    disabled={!isCurrentPublishedVersion || isDirty}
                    variant='basic'
                  >
                    Make Request
                  </Button>
                </div>
              </APICallButtonContainer>
              <TriggerLiveTestPayloadEditor
                payloadSample={outputJson}
                updatePayloadSample={setOutputJson}
              />
              {containsFileProperty && (
                <StyledBanner variant='warning'>
                  <CircleAlertIcon />
                  Live Test does not support file fields.
                </StyledBanner>
              )}
            </EditorWrapper>
          )}
        </MaximizablePanel>
      )}
      <Accordion title='Live Test' defaultExpanded={false}>
        <>
          <Spacer height={2} />
          <InstructionTextLarge>Trigger this workflow with a sample request</InstructionTextLarge>
          <HStack equal={true}>
            <Button
              id='trigger-config-api__api-show-modal'
              onClick={toggleSamplePayloadEditor}
              variant='basic'
            >
              Edit Payload
            </Button>
            <Button
              id='trigger-config-api__http-post-button'
              onClick={handlePostTestPayload}
              disabled={!isCurrentPublishedVersion || isDirty}
              variant='basic'
            >
              Make Request
            </Button>
          </HStack>
          <Spacer height={2} />
          <Accordion title='cURL Request' defaultExpanded={false}>
            <Spacer height={1} />
            <StyledCodeHighlight language='handlebars' light>
              {sampleCurlString.split('\n').slice(0, 40).join('\n')}
            </StyledCodeHighlight>
            <Spacer height={1} />
            <APIKeyInfoContainer>
              <InstructionTextSmall>
                Your API key is {isApiKeyHidden ? 'hidden by default.' : 'visible'}
              </InstructionTextSmall>
              <Button
                id='trigger-config-api__api-show-button'
                variant='basic'
                width='auto'
                disabled={!canViewApiKey}
                onClick={toggleAPIKeyHidden}
              >
                {isApiKeyHidden ? 'Show' : 'Hide'}
              </Button>
            </APIKeyInfoContainer>
          </Accordion>
          {(isDirty || !isCurrentPublishedVersion) && (
            <>
              <WarningText>Publish this Workflow before testing!</WarningText>
              <Spacer height={1} />
            </>
          )}
        </>
      </Accordion>
    </>
  );
};

export default TriggerLiveTest;
